import { makeStyles } from '@material-ui/styles';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button/Button';
import {
  grayColor,
  primaryColor,
  whiteColor,
} from 'assets/jss/material-dashboard-pro-react';
import { useTranslation } from 'react-i18next';
import { FakeUserIcon } from 'components';
import { NavLink } from 'react-router-dom';
import { useLayoutEffect, useMemo, useState } from 'react';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import moment from 'moment';
import CopyToClipboard from 'components/CopyToClipboard/CopyToClipboard';
import { fetchUserAddresses } from 'actions/Addresses';
import { useDispatch, useSelector } from 'react-redux';
import {
  ROLE_CREATE_EMAIL,
  ROLE_CREATE_SMS,
  ROLE_SHOW_MONEY_BOX_HISTORY,
  ROLE_SHOW_NOTE,
  ROLE_SHOW_SMS,
} from 'helpers/roles';
import { isGranted } from 'helpers/helpers';

import DietDetailsAddress from '../DietDetailsAddress';
import SmsModal from '../smsModal';
import NewEmailModal from '../NewEmailModal';
import NotesEditModal from '../notesEditModal';
import NotesModal from '../notesModal';
import DietDetailsMoneybox from '../DietDetailsMoneybox';

const useStyles = makeStyles({
  labelHorizontal: extendedFormsStyle.labelHorizontal,
  wrapper: {
    backgroundColor: whiteColor,
    display: 'flex',
    justifyContent: 'stretch',
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px',
    padding: '24px',
    fontWeight: '400',
  },
  clientWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flexGrow: '1',
    minHeight: '150px',
    paddingRight: '20px',
    borderRight: `1px solid ${grayColor[11]}`,
  },
  clientHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  clientContent: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '16px',
    marginBottom: '8px',
  },
  clientContentUser: {
    fontSize: '18px',
    fontWeight: '500',
    color: primaryColor[0],
  },
  clientContentUserId: {
    display: 'flex',
    alignItems: 'center',
    background: grayColor[13],
    fontSize: '18px',
    fontWeight: '500',
    padding: '4px 8px',
    marginBottom: '20px',
    borderRadius: '4px',
    width: 'fit-content',
  },
  clientContentUserIdLink: {
    marginRight: '8px',
    color: grayColor[16],
  },
  clientButtonNewOrder: {
    width: '100%',
  },
  addressesWrapper: {
    flexGrow: '1',
    minWidth: '200px',
    paddingLeft: '20px',
    paddingRight: '20px',
    borderRight: `1px solid ${grayColor[11]}`,
  },
  address: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    background: grayColor[13],
    borderRadius: '6px',
    padding: '8px',
    marginTop: '16px',
    fontSize: '14px',
    color: grayColor[1],
  },
  defaultAddressLabel: {
    gridColumn: '1',
    fontWeight: '500',
    marginBottom: '8px',
    color: grayColor[16],
  },
  addressLine: {
    gridColumn: '1',
  },
  copyToClipboard: {
    gridColumn: '2',
    gridRow: '1 / span 4',
    alignSelf: 'end',
  },
  contactWrapper: {
    flexGrow: '1',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    paddingLeft: '20px',
    paddingRight: '20px',
    borderRight: `1px solid ${grayColor[11]}`,
  },
  contactDivider: {
    margin: '6px 0 4px 0',
  },
  contactItem: {
    fontSize: '14px',
    marginBottom: '0',
  },
  contactSend: {
    fontSize: '14px',
    fontWeight: '500',
    padding: '2px 6px',
    margin: '-2px -6px',
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  contactEmailWrapper: { display: 'flex', justifyContent: 'space-between' },
  noticeWrapper: {
    flexGrow: '1',
    minWidth: '200px',
    paddingLeft: '20px',
    paddingRight: '20px',
    borderRight: `1px solid ${grayColor[11]}`,
  },
  noticeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  piggyBankWrapper: {
    minWidth: '150px',
    paddingLeft: '20px',
  },
  piggyBankHeader: { display: 'flex', justifyContent: 'space-between' },
  piggyBankContent: {
    fontSize: '14px',
    marginBottom: '0',
    marginTop: '16px',
  },
  label: {
    marginBottom: '0',
    color: grayColor[1],
  },
  headerButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: grayColor[1],
    cursor: 'pointer',
    fontSize: '12px',
    textTransform: 'none',
    textDecorationLine: 'underline',
    padding: '0',
  },
  noteWrapper: { maxHeight: '120px', overflowY: 'auto' },
  noteItem: {
    padding: '8px',
    background: grayColor[13],
    margin: '16px 0',
    borderRadius: '4px',
  },
  noteText: {
    fontSize: '10px',
    color: grayColor[1],
    marginBottom: '0',
  },
  noteContent: {
    margin: '16px 0',
    fontSize: '14px',
  },
});

const DietDetailsHeader = ({
  dietInfo,
  client,
  newOrder,
  notes,
  setNotes,
  setClient,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const addresses = useSelector(({ Addresses }) => Addresses.addresses);

  const [isAddressesModalStatus, setIsAddressesModalStatus] = useState(false);
  const [isSMSModalStatus, setIsSMSModalStatus] = useState(false);
  const [isEmailModalStatus, setIsEmailModalStatus] = useState(false);
  const [isOpenPiggyBankModal, setIsOpenPiggyBankModal] = useState(false);
  const [isOpenEditNotesModal, setIsOpenEditNotesModal] = useState(false);
  const [isOpenNotesModal, setIsOpenNotesModal] = useState(false);
  const [editModalData, setEditModalData] = useState({});

  const clientEditUrl = `/admin/clients/edit/${client.id}`;

  useLayoutEffect(() => {
    dispatch(fetchUserAddresses(client.id, false));
  }, [client.id, dispatch]);

  const defaultAddress = useMemo(
    () => addresses.find(address => address.isDefault),
    [addresses]
  );

  const addressToDisplay = defaultAddress || addresses[0];
  const addressValues = [
    `${addressToDisplay?.street} `,
    addressToDisplay?.buildNumber,
    addressToDisplay?.placeNumber ? `/${addressToDisplay?.placeNumber} ` : '',
    `${addressToDisplay?.postCode} `,
    `${addressToDisplay?.city} `,
    addressToDisplay?.zone?.name,
  ];

  return (
    <>
      <div className={classes.wrapper}>
        <div className={classes.clientWrapper}>
          <div className={classes.clientHeader}>
            <p className={classes.label}>{t('common.client')}</p>
          </div>
          <div className={classes.clientContent}>
            <NavLink className={classes.clientContentUser} to={clientEditUrl}>
              {client?.firstName || ''} {client?.lastName || ''}
            </NavLink>
            <FakeUserIcon
              email={client.email}
              domain={dietInfo.order?.comingFromDomain ?? client.homeDomain}
            />
          </div>
          <div className={classes.clientContentUserId}>
            <NavLink
              className={classes.clientContentUserIdLink}
              to={clientEditUrl}
            >{`ID #${client.id}`}</NavLink>
            <CopyToClipboard text={String(client.id)} />
          </div>
          <Button
            onClick={newOrder}
            className={classes.clientButtonNewOrder}
            variant="outlined"
          >
            {t('clients.newOrder')}
          </Button>
        </div>
        <div className={classes.addressesWrapper}>
          <div className={classes.clientHeader}>
            <p className={classes.label}>{t('clients.addresses')}</p>
            <button
              type="button"
              onClick={() => {
                setIsAddressesModalStatus(true);
              }}
              className={classes.headerButton}
            >
              {t('clients.manageAddresses')}
            </button>
          </div>
          {addresses.length > 0 && (
            <div className={classes.address}>
              {defaultAddress && (
                <span className={classes.defaultAddressLabel}>
                  {t('clients.defaultAddressLabel')}
                </span>
              )}
              <span className={classes.addressLine}>
                {addressValues[0]}
                {addressValues[1]}
                {addressValues[2]}
              </span>
              <span className={classes.addressLine}>
                {addressValues[3]}
                {addressValues[4]}
              </span>
              <span className={classes.addressLine}>{addressValues[5]}</span>
              <CopyToClipboard
                text={addressValues.join('')}
                classes={{ root: classes.copyToClipboard }}
              />
            </div>
          )}
        </div>
        <div className={classes.contactWrapper}>
          <p className={classes.label}>{t('clients.contactData')}</p>
          {client.phone?.number && (
            <div>
              <p className={classes.contactItem}>{client.phone.number}</p>
              <Divider className={classes.contactDivider} />
              {(isGranted(ROLE_SHOW_SMS) || isGranted(ROLE_CREATE_SMS)) && (
                <Button
                  className={classes.contactSend}
                  onClick={() => {
                    setIsSMSModalStatus(true);
                  }}
                >
                  {t('clients.writeSms')}
                </Button>
              )}
            </div>
          )}
          {client.email && (
            <div>
              <p className={classes.contactItem}>{client.email}</p>
              <Divider className={classes.contactDivider} />
              {isGranted(ROLE_CREATE_EMAIL) && (
                <Button
                  className={classes.contactSend}
                  onClick={() => {
                    setIsEmailModalStatus(true);
                  }}
                >
                  {t('clients.writeEmail')}
                </Button>
              )}
            </div>
          )}
        </div>
        {isGranted(ROLE_SHOW_NOTE) && (
          <div className={classes.noticeWrapper}>
            <div className={classes.noticeHeader}>
              <p className={classes.label}>{t('clients.notes')}</p>
              <button
                type="button"
                onClick={() => {
                  setIsOpenNotesModal(true);
                }}
                className={classes.headerButton}
              >
                {t('clients.more')}
              </button>
            </div>
            <div className={classes.noteWrapper}>
              {[...notes]
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map(note => (
                  <div key={note.id} className={classes.noteItem}>
                    <p className={classes.noteText}>
                      {moment(note.updatedAt).format('DD.MM.YYYY HH:mm')}
                    </p>
                    <p className={classes.noteContent}>{note?.content || ''}</p>
                    <p className={classes.noteText}>
                      {note.moderator?.firstName || ''}{' '}
                      {note.moderator?.lastName || ''}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        )}
        {isGranted(ROLE_SHOW_MONEY_BOX_HISTORY) && (
          <div className={classes.piggyBankWrapper}>
            <div className={classes.piggyBankHeader}>
              <p className={classes.label}>{t('clients.piggyBank')}</p>
              <button
                type="button"
                onClick={() => {
                  setIsOpenPiggyBankModal(true);
                }}
                className={classes.headerButton}
              >
                {t('clients.more')}
              </button>
            </div>
            <p className={classes.piggyBankContent}>
              {client.moneyBox} {t('orders.piggyBank.points')}
            </p>
          </div>
        )}
      </div>
      <DietDetailsAddress
        addressesModalStatus={isAddressesModalStatus}
        closeModal={() => {
          setIsAddressesModalStatus(false);
        }}
        client={client}
      />
      <SmsModal
        setSMSModalStatus={setIsSMSModalStatus}
        SMSModalStatus={isSMSModalStatus}
        client={client}
      />
      <NewEmailModal
        setEmailModalStatus={setIsEmailModalStatus}
        EmailModalStatus={isEmailModalStatus}
        client={client}
      />
      <NotesModal
        client={client}
        setNotes={setNotes}
        notesModalStatus={isOpenNotesModal}
        setNotesModalStatus={setIsOpenNotesModal}
        editModalStatus={isOpenEditNotesModal}
        setEditModalStatus={setIsOpenEditNotesModal}
        setEditModalData={setEditModalData}
        t={t}
      />
      {isOpenEditNotesModal && (
        <NotesEditModal
          client={client}
          setNotes={setNotes}
          editModalStatus={isOpenEditNotesModal}
          setEditModalStatus={setIsOpenEditNotesModal}
          setNotesModalStatus={setIsOpenNotesModal}
          editModalData={editModalData}
          t={t}
        />
      )}
      <DietDetailsMoneybox
        client={client}
        setClient={setClient}
        isOpenModal={isOpenPiggyBankModal}
        setIsOpenModal={setIsOpenPiggyBankModal}
      />
    </>
  );
};

export default DietDetailsHeader;
